import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApp } from "@/context/AppContext";
import { apiError } from "@/lib/api";
import { ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, register } = useApp();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user && user !== null && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        navigate("/");
      } else {
        await register(form);
        navigate("/setup");
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden flex-1 flex-col justify-between overflow-hidden border-r border-border bg-card p-12 lg:flex">
        <div className="grid-noise pointer-events-none absolute inset-0 opacity-60" />
        <div className="relative">
          <div className="flex h-10 w-10 items-center justify-center bg-primary font-display text-base font-extrabold text-primary-foreground">
            FX
          </div>
          <p className="mt-8 font-display text-xs font-bold uppercase tracking-[0.3em] text-muted-foreground">
            Ledger Zero
          </p>
        </div>
        <div className="relative max-w-xl">
          <h1 className="font-display text-4xl font-light leading-[1.05] tracking-tight lg:text-6xl">
            Replace the ledger.
            <br />
            <span className="font-extrabold">Reinvent the accountant.</span>
          </h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-muted-foreground">
            India's AI-first accounting & ERP platform. Double-entry that stays invisible, GST that files itself,
            cashflow that tells you what happens next.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-6 border-t border-border pt-6 text-xs">
          {[
            ["GST + e-Invoice", "IRN, QR, GSTR-1 & 3B"],
            ["AI Copilot", "Voice, NLQ, agents"],
            ["Bank Recon", "AI-matched feeds"],
          ].map(([t, s]) => (
            <div key={t}>
              <p className="font-display font-bold">{t}</p>
              <p className="text-muted-foreground">{s}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-[460px]">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="auth-form">
          <div>
            <h2 className="font-display text-2xl font-bold">
              {mode === "login" ? "Sign in to FinAxis" : "Create your workspace"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {mode === "login" ? "Use your FinAxis account." : "Starts a new tenant — you become the Owner."}
            </p>
          </div>

          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs uppercase tracking-widest">Your name</Label>
              <Input id="name" data-testid="register-name-input" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rahul Verma" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs uppercase tracking-widest">Email</Label>
            <Input id="email" type="email" data-testid="login-email-input" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.in" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs uppercase tracking-widest">Password</Label>
            <Input id="password" type="password" data-testid="login-password-input" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
          </div>

          {error ? (
            <p className="border-l-2 border-destructive bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="auth-error">
              {error}
            </p>
          ) : null}

          <Button type="submit" className="w-full gap-2" disabled={busy} data-testid="auth-submit-btn">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create workspace"}
            <ArrowRight size={15} />
          </Button>

          <button
            type="button"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            data-testid="auth-mode-toggle"
            className="w-full text-xs text-muted-foreground hover:text-foreground"
          >
            {mode === "login" ? "New to FinAxis? Create a workspace" : "Already have an account? Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
